import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Camera, Check, Loader2 } from 'lucide-react';
import { compressImage, uploadAttachment } from '../../lib/image';

export interface ChecklistStep {
  id: string;
  stepNumber: number;
  instruction: string;
  isRequired: boolean;
  requirePhoto: boolean;
  isCompleted: boolean;
  photoAttachmentId: string | null;
  notes?: string | null;
}

export interface ChecklistStepRowProps {
  step: ChecklistStep;
  disabled: boolean;
  onToggle: (isCompleted: boolean) => Promise<void>;
  onAttachPhoto: (attachmentId: string) => Promise<void>;
  onSaveNotes: (notes: string) => Promise<void>;
}

/**
 * One SOP step.
 *
 * A step that expects a photo says so and offers the camera, but the tick is never
 * gated on it (decision D12) — a technician whose camera will not open in a plant
 * room must still be able to sign off work they have genuinely done. The missing
 * proof is surfaced here and counted on the dashboard instead.
 */
export function ChecklistStepRow({
  step,
  disabled,
  onToggle,
  onAttachPhoto,
  onSaveNotes,
}: ChecklistStepRowProps) {
  const { t } = useTranslation();
  const fileInput = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState(step.notes ?? '');
  const [error, setError] = useState<string | null>(null);

  const proofMissing = step.isCompleted && step.requirePhoto && !step.photoAttachmentId;

  async function guarded(action: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t('checklist.failed'));
    } finally {
      setBusy(false);
    }
  }

  async function handlePhoto(files: FileList | null) {
    const file = files?.[0];
    if (!file) return;

    await guarded(async () => {
      const compressed = await compressImage(file);
      const id = await uploadAttachment(compressed.blob, `step-${step.stepNumber}.webp`);
      await onAttachPhoto(id);
    });
    if (fileInput.current) fileInput.current.value = '';
  }

  return (
    <li className="rounded-lg bg-slate-900 p-3">
      <div className="flex items-start gap-3">
        <input
          type="checkbox"
          checked={step.isCompleted}
          disabled={disabled || busy}
          aria-label={step.instruction}
          onChange={(event) => void guarded(() => onToggle(event.target.checked))}
          className="mt-0.5 size-6 min-h-0 shrink-0 accent-emerald-500"
        />

        <div className="min-w-0 flex-1">
          <p
            className={`text-sm ${step.isCompleted ? 'text-slate-400 line-through' : 'text-slate-100'}`}
          >
            {step.instruction}
            {!step.isRequired && (
              <span className="ml-1 text-xs text-slate-500">{t('checklist.optional')}</span>
            )}
          </p>

          {step.requirePhoto && (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {step.photoAttachmentId ? (
                <span className="inline-flex items-center gap-1 text-xs text-emerald-400">
                  <Check className="size-3" aria-hidden="true" />
                  {t('checklist.photoAttached')}
                </span>
              ) : (
                <button
                  type="button"
                  disabled={disabled || busy}
                  onClick={() => fileInput.current?.click()}
                  className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-slate-600 bg-transparent px-3 text-xs text-slate-200"
                >
                  {busy ? (
                    <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                  ) : (
                    <Camera className="size-4" aria-hidden="true" />
                  )}
                  {t('checklist.addPhoto')}
                </button>
              )}

              {proofMissing && (
                <span className="text-xs text-amber-400">{t('checklist.proofMissing')}</span>
              )}
            </div>
          )}

          <input
            ref={fileInput}
            type="file"
            accept="image/*"
            capture="environment"
            className="sr-only"
            aria-label={t('checklist.addPhoto')}
            onChange={(event) => void handlePhoto(event.target.files)}
          />

          <input
            value={notes}
            disabled={disabled}
            placeholder={t('checklist.notesPlaceholder')}
            aria-label={t('checklist.notesPlaceholder')}
            maxLength={1000}
            onChange={(event) => setNotes(event.target.value)}
            // Saved on blur rather than per keystroke: one write per edit on a 3G link.
            onBlur={() => {
              if (notes !== (step.notes ?? '')) void guarded(() => onSaveNotes(notes));
            }}
            className="mt-2 w-full rounded border border-slate-700 bg-slate-950 px-2 py-2 text-xs text-slate-200"
          />

          {error && (
            <p role="alert" className="mt-1 text-xs text-red-400">
              {error}
            </p>
          )}
        </div>
      </div>
    </li>
  );
}
